import React, { useState } from 'react';
import { Braces, Check, ChevronRight, Copy, List, Loader2, RefreshCw, Wrench, X } from 'lucide-react';
import {
  formatToolPayload,
  pairToolCalls,
  type PlaygroundTrace,
  type PlaygroundTraceEntry,
} from '../../utils/toolCalls';

/**
 * Playground tool-call panel — shows WHY the agent answered the way it did.
 *
 * 🚨 WHY THIS IS A SIDE PANEL AND NOT ROWS INSIDE THE CHAT.
 * `Playground.tsx` is an iframe wrapper around the PRODUCTION widget; it renders
 * no transcript of its own. Interleaving tool rows between the widget's bubbles
 * would mean editing `Widget.tsx` — a PROTECTED file (feature #1: chat + voice +
 * avatar). The owner chose this design specifically to avoid that. So the panel
 * lives in the Playground chrome, beside the iframe, and joins to the transcript
 * by `message_index` rather than trying to sit inside it.
 *
 * 🚨 `entries` IS SPARSE. The backend omits messages that made no tool calls, so
 * it is NOT an array parallel to the transcript. Entries are keyed and labelled
 * by `message_index` — never by array position. Sorted DESCENDING so the turn the
 * tester just triggered is at the top, where they are already looking.
 *
 * ⚠️ Tool payloads contain REAL CUSTOMER PII (contact emails, full Shopify order
 * records). That is intended — this is a staff-only instrument behind a cookie +
 * workspace-membership gate — but it is why the data must never reach the
 * customer chat response or the (unauthenticated) widget WebSocket.
 */

interface ToolCallPanelProps {
  trace: PlaygroundTrace | null;
  /** First load for this session — drives the spinner. Polling refreshes must
   *  NOT set this, or the panel would flicker every few seconds. */
  loading: boolean;
  /** The trace endpoint 404'd. It is the DENIAL code (never 403 — a 403 would
   *  confirm the conversation exists), so this covers an expired staff session,
   *  a non-member, an unknown session and a non-playground conversation alike.
   *  It is a normal state, not an error: we say "no trace available" and move on. */
  unavailable: boolean;
  /** No session to trace yet (nothing sent in the widget). */
  hasSession: boolean;
  onRefresh: () => void;
  onClose: () => void;
}

const ToolCallEntry: React.FC<{ entry: PlaygroundTraceEntry }> = ({ entry }) => {
  const calls = pairToolCalls(entry.tool_calls);
  if (calls.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2.5">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
        {/* `message_index` is the JOIN KEY into conversations.messages[] — shown
            so a tester can line an entry up with the bubble it belongs to. */}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Message #{entry.message_index}
        </span>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          {calls.length} call{calls.length === 1 ? '' : 's'}
          {entry.response_time_ms != null && (
            <> · {(entry.response_time_ms / 1000).toFixed(1)}s</>
          )}
        </span>
      </div>

      <div className="space-y-2">
        {calls.map((c, i) => (
          <details key={i} className="group rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <summary className="flex items-center gap-1.5 cursor-pointer list-none px-2 py-1.5 text-[11px] font-medium text-gray-700 dark:text-gray-200">
              <ChevronRight className="w-3 h-3 flex-shrink-0 transition-transform group-open:rotate-90" />
              <code className="font-semibold text-indigo-700 dark:text-indigo-300 [overflow-wrap:anywhere] min-w-0">
                {c.name}
              </code>
              {c.pending && (
                <span className="ml-auto flex-shrink-0 text-[10px] font-medium text-amber-700 dark:text-amber-300">
                  no result
                </span>
              )}
            </summary>

            <div className="px-2 pb-2 space-y-1.5">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Arguments
                </div>
                <pre className="mt-0.5 text-[11px] leading-snug rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-800 dark:text-gray-200 p-1.5 overflow-x-auto whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {formatToolPayload(c.args)}
                </pre>
              </div>

              {!c.pending && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Response
                  </div>
                  {/* Truncated mid-token by the 1000-char capture cap? Then
                      `formatToolPayload` hands back the raw partial string
                      rather than throwing. */}
                  <pre className="mt-0.5 text-[11px] leading-snug rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-800 dark:text-gray-200 p-1.5 overflow-x-auto whitespace-pre-wrap [overflow-wrap:anywhere]">
                    {formatToolPayload(c.response)}
                  </pre>
                </div>
              )}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

/**
 * Raw-JSON view of ONE turn's captured `tool_calls` array. The owner wants both
 * the structured read AND the untouched payload — the structured view can pair
 * / truncate for readability, so this is the escape hatch when a tester needs
 * the exact bytes (e.g. to paste a repro). Pretty-printed, scrollable, copyable.
 */
const RawJsonEntry: React.FC<{ entry: PlaygroundTraceEntry }> = ({ entry }) => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(entry.tool_calls, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure origin / denied). The <pre> text is
      // still selectable, so the tester can copy manually — degrade silently.
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Message #{entry.message_index}
        </span>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Copy raw JSON"
          aria-label="Copy raw JSON"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="text-[11px] leading-snug rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-200 p-1.5 overflow-x-auto whitespace-pre [overflow-wrap:normal]">
        {json}
      </pre>
    </div>
  );
};

type PanelView = 'structured' | 'json';

const ToolCallPanel: React.FC<ToolCallPanelProps> = ({
  trace,
  loading,
  unavailable,
  hasSession,
  onRefresh,
  onClose,
}) => {
  // Structured (paired name/args/response) vs raw JSON. Owner wants both.
  const [view, setView] = useState<PanelView>('structured');
  // Newest turn first. Copy before sorting — never mutate the fetched array.
  const entries = [...(trace?.entries ?? [])].sort(
    (a, b) => b.message_index - a.message_index,
  );
  const totalCalls = entries.reduce(
    (n, e) => n + pairToolCalls(e.tool_calls).length,
    0,
  );

  return (
    <div className="w-full h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 min-w-0">
          <Wrench className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">Tool Calls</span>
          {totalCalls > 0 && (
            <span className="flex-shrink-0 px-1.5 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 normal-case tracking-normal">
              {totalCalls}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onRefresh}
            className="p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Refresh trace"
            aria-label="Refresh trace"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {/* Close is the escape hatch on mobile, where the panel is an overlay
              drawer. Kept on desktop too so the tester can reclaim the width. */}
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            title="Hide tool calls"
            aria-label="Hide tool calls"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* View toggle — only meaningful once there are entries to show. A
          full-width sub-header row so it never competes with the action
          buttons above at 375px (the drawer is only ~85vw wide on mobile). */}
      {!loading && hasSession && !unavailable && entries.length > 0 && (
        <div className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700">
          <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setView('structured')}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium transition-colors ${
                view === 'structured'
                  ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              aria-pressed={view === 'structured'}
            >
              <List className="w-3 h-3" />
              Structured
            </button>
            <button
              onClick={() => setView('json')}
              className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium transition-colors border-l border-gray-200 dark:border-gray-700 ${
                view === 'json'
                  ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              aria-pressed={view === 'json'}
            >
              <Braces className="w-3 h-3" />
              JSON
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 px-2 py-3">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading trace…
          </div>
        ) : !hasSession ? (
          <p className="text-xs text-center text-gray-600 dark:text-gray-300 py-6 px-2">
            Send a message in the widget — the agent's tool calls will appear here.
          </p>
        ) : unavailable ? (
          // 404 from the staff-gated endpoint. Most likely an expired session;
          // could also be a conversation this member cannot access. Either way
          // it is not an error to shout about.
          <p className="text-xs text-center text-gray-600 dark:text-gray-300 py-6 px-2">
            No trace available for this session.
          </p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-center text-gray-600 dark:text-gray-300 py-6 px-2">
            No tool calls in this conversation yet. The agent answered from its
            prompt and knowledge alone.
          </p>
        ) : view === 'json' ? (
          entries.map((e) => (
            <RawJsonEntry key={e.message_index} entry={e} />
          ))
        ) : (
          entries.map((e) => (
            <ToolCallEntry key={e.message_index} entry={e} />
          ))
        )}
      </div>
    </div>
  );
};

export default ToolCallPanel;
